// Скрипты эффектов: карты, жетоны колдунских свойств, жетоны дохлых колдунов, беспределы.
// Каждая карта/жетон описывает себя целиком здесь: эффект при розыгрыше (CARD_EFFECTS), активация ☀ (ACTIVATIONS),
// защита (DEFENSE_EFFECTS), пассивные свойства (PASSIVES / TRAITS / DDK_PASSIVES — ядро спрашивает их через hooks.ts).
import type { CardId, CardInst, CardType, DdkDef, Game, Player, PlayerZone, PromptOption, TraitKey } from '../types.ts';
import { legendMarketSizeOf } from './mutators.ts';
import {
  card, log, enemies, leftOf, rightOf, clockwiseFrom, drawCards, gainChips, spendChips, gainCard, gainLimp,
  trashCard, removeFrom, maxLife, revealTop, controlled, isWand, isLegend, player, placeMarketChip, addMarketChip,
  ddkDef, rngOf, turnOf, hasTag, isBespredel, makeInstance, giveCard, discardCards, moveCards, emit,
} from './state.ts';
import { RULES, STARTER_DECK, reb, circusFlipsAll, swapLivesBeforeCap, holesterinDohlyak } from './rules.ts';
import * as E from './effects.ts';
import type { Eff, EffectCtx } from './effects.ts';
import type { CardPassive, DdkPassive, TraitPassive } from './hooks.ts';
import * as H from './hooks.ts';
import { shuffle, randInt } from './rng.ts';
import { rebalanced, rebalancedTrait } from './rebalance.ts';

// ---------- типы скриптов ----------
export interface PlayOpts {
  fromEnemyDeck?: boolean;
}
/** Эффект карты при розыгрыше */
export type CardEffect = (G: Game, p: Player, c: CardInst, opts: PlayOpts) => Eff;
/** Активация ☀ карты под контролем */
export type Activation = (G: Game, p: Player, c: CardInst) => Eff;
/** Результат защиты: false — защита не состоялась, {redirect} — атака перенаправлена на атакующего */
export type DefenseResult = boolean | { redirect: true };
export type DefenseEffect = (G: Game, ctx: EffectCtx, p: Player, dc: CardInst) => Eff<DefenseResult>;
export type Condition = (G: Game, p: Player, c: CardInst) => boolean;
export type DdkEffect = (G: Game, ctx: EffectCtx, p: Player, t: DdkDef) => Eff;
export type BespredelEffect = (G: Game, active: Player, bctx: EffectCtx) => Eff;

// ---------- хелперы ----------
const ctx = (p: Player, c: CardInst, extra: Partial<EffectCtx> = {}): EffectCtx => ({ source: p, card: c, isAttack: true, ...extra });

export function countCtl(G: Game, p: Player, type: CardType): number {
  return controlled(G, p).filter((c) => H.typeMatches(c, type)).length;
}
/** Жетоны дохлых колдунов у игрока, включая дохляков-постоянок (они считаются жетонами) */
export function ddkCount(_G: Game, p: Player): number {
  return p.ddk.length + p.permanents.filter((c) => hasTag(c, 'deadWizard')).length;
}
/** Снять жетон дохлого колдуна: to — кому передаётся (null — уничтожается); журнал и событие ddkLost */
export function removeDdk(G: Game, p: Player, id: string, to: Player | null = null): boolean {
  const i = p.ddk.indexOf(id);
  if (i < 0) return false;
  p.ddk.splice(i, 1);
  const t = ddkDef(id);
  log(G, `${p.name} ${to ? `отдаёт ${to.name}` : 'уничтожает'} жетон дохлого колдуна: «${t.text || '—'}»`);
  emit(G, { type: 'ddkLost', pid: p.id, tid: id, text: t.text || '', to: to ? to.id : null });
  return true;
}
function maxCostCtl(G: Game, p: Player, exceptIid: number | null = null): number {
  const cs = controlled(G, p).filter((c) => c.iid !== exceptIid);
  return cs.length ? Math.max(...cs.map((c) => card(c).cost ?? 0)) : 0;
}
const costOf = (c: CardInst): number => card(c).cost ?? 0;

/** Раскрыть верхнюю карту своей колоды. silent — не показывать отдельный экран: карта будет показана в следующем вопросе. */
function* revealOwnTop(G: Game, p: Player, { silent = false } = {}): Eff<CardInst | null> {
  const c = revealTop(G, p);
  if (c) {
    log(G, `${p.name} раскрывает верхнюю карту колоды: «${card(c).name}»`);
    if (!silent) yield* E.info(G, p, 'Верхняя карта колоды', `«${card(c).name}» (стоимость ${card(c).cost ?? 0})`, { cards: [E.cardRef(c)] });
  }
  return c;
}
/** Легенды среди cards для НЕВЫГОДНОГО эффекта: настоящие легенды — всегда; фамильяров при жетоне «три фамильяра»
 *  («ты всегда можешь считать своих фамильяров ещё и легендами») игрок выбирает сам — кого считать, кого нет.
 *  Для выгодных эффектов выбор не нужен — там H.isLegendFor */
function* legendsByChoice(G: Game, p: Player, cards: readonly CardInst[], title: string, zoneLabel: string): Eff<CardInst[]> {
  const real = cards.filter((x) => isLegend(x));
  if (!H.familiarsAreLegends(p)) return real;
  const fams = cards.filter((x) => card(x).type === 'фамильяр');
  if (!fams.length) return real;
  const picked = yield* E.chooseCards(G, p, fams, { min: 0, max: fams.length, title, zoneLabel, kind: 'asLegend' });
  if (picked.length) log(G, `${p.name} считает легендами: ${picked.map((x) => `«${card(x).name}»`).join(', ')}`);
  return [...real, ...picked];
}
/** «Свести жизни к N»: привести к значению в любую сторону (в пределах максимума) */
function setLife(G: Game, p: Player, n: number): void {
  const target = Math.min(n, maxLife(p));
  if (target > p.life) E.heal(G, p, target - p.life);
  else if (target < p.life) E.setLifeTo(G, p, target, 'сведены');
  else log(G, `${p.name}: жизни и так ${target}`);
}

/**
 * Карты, для которых важен порядок розыгрыша («если это первая сыгранная карта в ходу»): пока такая карта на руке,
 * движок не разыгрывает простые карты сам — игрок решает, что пойдёт первым.
 */
export const ORDER_SENSITIVE: ReadonlySet<CardId> = new Set<CardId>(['vurdalakar']);

/**
 * Карты, чей эффект работает с картами на руке (Шлем-тотем: уничтожить карту с руки; Та самая вялая палочка: отдать
 * жертве вялые палочки с руки). Пока такая карта на руке, простые карты сами не разыгрываются — иначе к моменту
 * розыгрыша уничтожать/отдавать будет нечего. Как только все такие карты сыграны, автоматика продолжает.
 */
export const HAND_SENSITIVE: ReadonlySet<CardId> = new Set<CardId>(['shlem-totem', 'ta-samaya-vyalaya-palochka']);

/**
 * Постоянки, чья ☀ уничтожает карты с руки (Волшебные отходы). Пока такая ☀ доступна, а игрок в этом ходу ещё
 * ничего не сыграл, простые карты сами не разыгрываются — иначе автоматика сыграла бы как раз то, что он хотел
 * уничтожить. Первая карта, сыгранная руками, запускает автоматику (см. autoPlay в game.ts). Спиногрызогрызов
 * здесь нет: это не постоянка, на столе они оказываются, только если их уже сыграли в этом ходу.
 */
export const TABLE_HAND_TRASH: ReadonlySet<CardId> = new Set<CardId>(['volshebnye-otkhody']);

/**
 * Карты, которыми можно уничтожить карты с руки по ☀ (на столе или сыграв их с руки): Волшебные отходы,
 * Спиногрызогрызы. Пока такая возможность есть, автоматика не играет карты с добором (REVEALS_ON_PLAY): добор
 * раскрывает колоду и закрывает «Отмену» авто-розыгрыша — и хлам, сыгранный автоматикой, уже не вернуть в руку,
 * чтобы уничтожить (с v17, см. autoPlay в game.ts).
 */
export const HAND_TRASHERS: ReadonlySet<CardId> = new Set<CardId>(['volshebnye-otkhody', 'spinogryzogryzy']);

/**
 * Простые карты, чей розыгрыш раскрывает скрытое (добор): автоматика их не играет (в старых записях играла — тогда после них «Отмена» авто-розыгрыша невозможна). Список
 * сверяется с движком тестом (test/autoplay.ts): каждая карта, разыгранная автоматикой, раскрывает скрытое тогда
 * и только тогда, когда она здесь (или revealsOnPlay говорит так для режима).
 */
export const REVEALS_ON_PLAY: ReadonlySet<CardId> = new Set<CardId>([
  'serdce-maga-po-epichnomu', 'legendohranitel', 'nadyozhnyj-plan', 'besopanki', 'mozgoshtorm', 'sherif-muhtar',
  'muertito-knizhnyj-cherv', 'myzhsemya', 'konduktor-zhmi-na-tormoza', 'nikchemuhazhery',
]);

/** Раскрывает ли розыгрыш карты скрытое в этой партии (в режиме ребаланса Зад в будущее ещё и добирает) */
export function revealsOnPlay(G: Game, cid: CardId): boolean {
  return REVEALS_ON_PLAY.has(cid) || (cid === 'zad-v-budushchee' && reb(G, 2));
}

/** Есть ли на руке карта, из-за которой простые карты нельзя разыгрывать автоматически (порядок или состав руки) */
export function holdsAutoPlay(hand: readonly CardInst[]): boolean {
  return hand.some((x) => ORDER_SENSITIVE.has(x.cid) || HAND_SENSITIVE.has(x.cid));
}

/**
 * Карты, которые движок НЕ разыгрывает за игрока: нужен выбор или важен момент розыгрыша. Все остальные карты без
 * атаки (мощь, добор, чипсины, постоянки, карты с ☀ — сама активация отдельно) играются автоматически, как только
 * оказываются на руке (см. autoPlay в game.ts). Атаки всегда вручную: цель и порядок ударов выбирает игрок.
 */
export const MANUAL_PLAY: ReadonlySet<CardId> = new Set<CardId>([
  // карта задаёт вопрос
  'shalnaya-magiya', 'bratstvo-kontsa', 'govna-piroga', 'sdelka-so-zhgunom', 'shlem-totem', 'smert-ot-holesterina',
  'surkovyy-agent', 'vosstanie-iz-zada', 'zozhlozhniki',
  // важен момент: «первая карта в ходу»; какую атаку сделать неизбежной
  'vurdalakar', 'benzopila-druzhok',
  // мощь/чипсины зависят от того, что сыграно или случилось раньше в этом ходу
  'vedmin-podarochek', 'priunyvshij-ork', 'loshashlyk',
]);

/** Разыгрывает ли движок эту карту за игрока сам */
export function isAutoPlay(cid: CardId): boolean {
  const d = card(cid);
  if (isBespredel(cid) || d.attack) return false;
  return !MANUAL_PLAY.has(d.id);
}

/**
 * «Ванильная» карта: только мощь — нет ни эффекта, ни пассива, ни атаки/защиты/активации/постоянки
 * (Знак, Пшик, Пивохранилище…). О таких не стоит уведомлять: они лишь захламляют ленту.
 */
export function isVanilla(cid: CardId): boolean {
  const d = card(cid);
  if (isBespredel(cid) || d.attack || d.defense || d.activation || d.permanent) return false;
  if (PASSIVES[d.id]) return false;
  const fx = CARD_EFFECTS[d.id];
  return !fx || fx === noop;
}

function trashOwnTop(G: Game, p: Player, c: CardInst): void {
  removeFrom(p.deck, c.iid);
  trashCard(G, c, p);
  log(G, `${p.name} уничтожает «${card(c).name}»`);
}

// ---------- розыгрыш карты ----------
export function* playCardEffect(G: Game, p: Player, c: CardInst, opts: PlayOpts = {}): Eff {
  const def = card(c);
  if (def.power) E.addPower(G, def.power, def.name);
  H.onPlay(G, p, c);
  const fx = CARD_EFFECTS[def.id];
  if (fx) yield* fx(G, p, c, opts);
}

/** Стартовая колода по умолчанию (жетон свойства может её изменить) */
export function defaultStarterDeck(mk: (cid: CardId) => CardInst = makeInstance): CardInst[] {
  const deck: CardInst[] = [];
  for (let i = 0; i < STARTER_DECK.signs; i++) deck.push(mk(STARTER_DECK.sign));
  deck.push(mk(STARTER_DECK.wand));
  for (let i = 0; i < STARTER_DECK.blanks; i++) deck.push(mk(STARTER_DECK.blank));
  return deck;
}

// ---------- ПАССИВНЫЕ СВОЙСТВА КАРТ ----------
export const PASSIVES: Record<CardId, CardPassive> = {
  'smazochnaya-gryaznaya-palka': {
    onPlay(G, _p, played) { if (isWand(played)) E.addPower(G, 1, 'Смазочная грязная палка'); },
    modifyDamage(_G, c, _t, n) { return c.card && isWand(c.card) ? n + 2 : n; },
  },
  'chipsihoz-arena': {
    modifyDamage(_G, c, target, n) { return c.source && target.id !== c.source.id ? n * 2 : n; },
  },
  'park-vurdalakcionov': {
    afterDamage(G, c, target, n) {
      const src = c.source;
      if (src && G.turn && src.id === G.turn.player && target.id !== src.id) E.heal(G, src, n);
    },
    handLimit(_G, p) { return p.life >= maxLife(p) ? 2 : 0; },
  },
  'ultimativnoe-tronado': {
    afterAttack(G, c) {
      const src = c.source;
      const dealt = c.dealt ?? 0;
      if (!src || !G.turn || src.id !== G.turn.player || dealt <= 0 || G.turn.tronadoUsed) return;
      G.turn.tronadoUsed = true;
      E.addPower(G, dealt, 'Ультимативное тронадо');
    },
  },
  'viagrus-tvyordyj-lord': {
    onPlay(G, _p, played) { if (card(played).type === 'вялая палочка') E.addPower(G, reb(G, 7) ? 2 : 3, 'Виагрус'); },
    *startOfTurn(G, p) {
      // ребаланс: без ежеходной палочки из стопки (движок «палочки в плюс» кормится только покупными/чужими) —
      // по лаборатории вариантов винрейт 48% → 41% (docs/card-variants-extra.md)
      if (reb(G, 1) || !G.limpPile.length) return;
      const yes = yield* E.confirm(G, p, 'Виагрус: взять 1 вялую палочку на руку?');
      if (yes) gainLimp(G, p, 'hand');
    },
    limpPositive: true,
  },
  'vyalaya-bashnya': {
    startPower() { return 1; },
    onGained(G, p) { gainLimp(G, p); gainLimp(G, p); },
  },
  'zhelatinovyy-sisyak': { startPower() { return 1; } },
  'pohotlivyj-mayachok': { startPower(G, p) { return ddkCount(G, p); } },
  'tsirk-bratev-loshashnykh': {
    startPower(_G, p) { return p.loser ? 2 : 0; },
    // «штрафные ПО за жетон лошары становятся бонусными»: строка «Лошара −5» остаётся, Цирк кладёт двойную сумму штрафа
    // (−5 → +5: +10). Ребаланс: бонус 6 вместо 10 — даже за 9 карта держала +5..+11 к цене, цена не дожимала (волна 3).
    // С v15 переворачивается и удвоение от ЖДК k12 «удвой штрафные ПО за жетон лошары»: −5 −5 +20 = +10 (было +10 → 0)
    endGameVP(G, p) {
      if (!p.loser) return null;
      const flip = reb(G, 7) ? 8 : reb(G, 3) ? 6 : 10;
      const doubled = circusFlipsAll(G) && p.ddk.some((id) => ddkDef(id).key === 'k12');
      return doubled
        ? { label: 'Цирк: штраф лошары (×2) → бонус', value: flip - 2 * RULES.loserPenalty }
        : { label: 'Цирк: штраф лошары → бонус', value: flip };
    },
  },
  'dlan-tvortsa': { handLimit() { return 1; } },
  'suvenirnyj-laryok-v-zale-legend': {
    gainDestinations(_G, p, gained) { return H.isLegendFor(p, gained) ? [{ id: 'deckTop', label: 'На верх колоды (Сувенирный ларёк)' }] : []; },
  },
  'gusynya-koroleva-mat': {
    endGameVP(G, _p, c) {
      // ребаланс: не больше 6 ПО — без капа снежок «легенды кормят Гусыню» давал купившим 44% побед (лаборатория: кап → 33%)
      const raw = 2 * c.legends;
      const cap = reb(G, 7) ? 8 : reb(G, 4) ? 10 : reb(G, 1) ? 6 : Infinity;
      const value = Math.min(cap, raw);
      return { label: `Гусыня: +2 ПО за легенду${cap < Infinity ? ` (не больше ${cap})` : ''}`, value };
    },
  },
  'potnyj-gikpig': { endGameVP(_G, _p, c) { return { label: 'Потный Гикпиг: +1 ПО за тварь', value: c.owned.filter((x) => card(x).type === 'тварь').length }; } },
  'serdce-maga-po-epichnomu': { endGameVP() { return { label: 'Сердце мага (в протекторе)', value: 2 }; } },
};
// правки режима ребаланса в пассивках выше (ветки reb(G, версия)) и их описания для UI — в одном месте
// волна 4.2: даже за 15 карта даёт +16.3±7.1 Δточка — цена по ней не работает (урок «снежков»), режем сам мотор
rebalanced('viagrus-tvyordyj-lord', 'rework', 'не берёт палочку из стопки в начале хода; за палочку +2 мощи вместо +3',
  'Постоянка.\nВсякий раз, когда играешь вялую палочку, +2 мощи.\nВ конце игры: твои вялые палочки приносят тебе ПО, а не отнимают их.');
rebalanced('tsirk-bratev-loshashnykh', 'nerf', 'бонус лошары в конце игры 8 ПО вместо 10',
  'Постоянка: если ты лошара, +2 мощи.\nВ конце игры: если ты лошара, штрафные ПО за жетон лошары не считаются, и ты получаешь +3 ПО.');
rebalanced('gusynya-koroleva-mat', 'nerf', 'бонус «+2 ПО за легенду» ограничен 8 ПО',
  '+1 мощь\n+2 ПО за каждую легенду, которой ты владеешь (не больше 8 ПО).');

// ---------- ЖЕТОНЫ КОЛДУНСКИХ СВОЙСТВ ----------
export const TRAITS: Record<TraitKey, TraitPassive> = {
  treasureDiscount: {
    discount(def) { return def.type === 'сокровище' ? 1 : 0; },
    endGameVP(_G, _p, c) {
      const t = c.owned.filter((x) => card(x).type === 'сокровище').length;
      return t ? { label: 'Жетон свойства: +1 ПО за сокровище', value: t } : null;
    },
  },
  wizardChip: {
    gainDestinations(G, _p, gained) { return reb(G, 8) && card(gained).type === 'волшебник' ? [{ id: 'deckTop', label: 'На верх колоды (жетон свойства)' }] : []; },
    *onGain(G, p, gained) {
      if (card(gained).type !== 'волшебник') return;
      gainChips(G, p, 1, 'свойство: волшебник');
      if (p.loser) {
        const yes = yield* E.confirm(G, p, 'Перестать быть лошарой?');
        if (yes) yield* E.stopLoser(G, p);
      }
    },
  },
  hrenalochka: {
    starterDeck(mk) {
      const deck = [mk(STARTER_DECK.hrenalochka)];
      for (let i = 0; i < STARTER_DECK.signs - 1; i++) deck.push(mk(STARTER_DECK.sign));
      deck.push(mk(STARTER_DECK.wand));
      for (let i = 0; i < STARTER_DECK.blanks; i++) deck.push(mk(STARTER_DECK.blank));
      return deck;
    },
    modifyDamage(G, c, _t, n) { return c.card && isWand(c.card) ? n + (reb(G, 8) ? 2 : 1) : n; },
    wandsUnavoidable: true,
  },
  permChip: {
    onPlay(G, p, played) { if (card(played).permanent) gainChips(G, p, reb(G, 8) ? 2 : 1, 'свойство: постоянка'); },
    gainDestinations(_G, _p, gained) { return card(gained).permanent ? [{ id: 'deckTop', label: 'На верх колоды (жетон свойства)' }] : []; },
  },
  // ребаланс: приз на старте остаётся (без него свойство пустое: −8.1), бонус жизней тоже (волна 4.5:
  // без него свойство просело до −3.3) — снято только право первого хода (см. beginTurn)
  // история версий: 4–7 жизни 22, 8 — без стартового приза, 9 — жизни 20 (см. RULES_VERSION)
  prize: {
    life: (G) => (reb(G, 4, 7) ? RULES.prizeOwnerLifeV4 : reb(G, 9, 9) ? RULES.startLife : RULES.prizeOwnerLife),
    startsWithPrize: true,
    startsWithPrizeIn: (G) => !reb(G, 8, 8),
  },
  act6: {
    activation: {
      id: 'trait-act6', label: 'Свойство ☀: +1 чипсина', hint: 'Нужно контролировать хотя бы 2 сокровища/твари',
      condition: (G, p) => countCtl(G, p, 'сокровище') + countCtl(G, p, 'тварь') >= 2,
      auto: true, // чистая чипсина без решений — срабатывает сама
      *run(G, p) { gainChips(G, p, reb(G, 4) ? 2 : 1, 'свойство ☀'); },
    },
  },
  spellHand: { extraDraw(G) { return turnOf(G).spellsGained; } },
  beastTop: {
    gainDestinations(_G, _p, gained, fromMarket) { return fromMarket && card(gained).type === 'тварь' ? [{ id: 'deckTop', label: 'На верх колоды (жетон свойства)' }] : []; },
    *onGain(G, p, gained) { if (reb(G, 4) && card(gained).type === 'тварь') gainChips(G, p, 1, 'свойство: тварь'); },
  },
  threeFamiliars: { familiarsAreLegends: true, threeFamiliars: true },
  act10: {
    activation: {
      id: 'trait-act10', label: 'Свойство ☀: сыграть карту врага', hint: 'Нужно контролировать хотя бы 2 волшебников/заклинания',
      condition: (G, p) => countCtl(G, p, 'волшебник') + countCtl(G, p, 'заклинание') >= (reb(G, 4, 4) ? 1 : 2),
      *run(G, p) {
        if (reb(G, 5)) gainChips(G, p, 1, 'свойство ☀');
        const e = yield* E.pickEnemy(G, p, 'Жетон свойства: с чьей колоды сыграть верхнюю карту?');
        if (e) yield* E.playTopOfEnemyDeck(G, p, e);
      },
    },
  },
};

// правки режима ребаланса в свойствах выше (ветки reb(G, версия)) и их описания для UI — в одном месте.
// Основание — контрфактика свойств (tools/traits.ts, 112 точек × 10 свойств, парно): prize +10.1% побед
// против среднего свойства, act6 −3.5, beastTop −3.6, act10 −3.9 (остальные в пределах ±2).
// волна 4.3/4.4 (288 точек на замер): срез жизней 25 → 22 почти не подействовал (+10.1 → +8.9), а полное
// снятие приза оказалось перенерфом (−8.1) — свойство без приза пустое. Оставляем приз, убираем довески.
rebalancedTrait('prize', 'nerf', 'не ходишь первым',
  `Начни игру с главным призом. Если ты не лошара, начальное значение твоих жизней (даже при воскрешении) равно ${RULES.prizeOwnerLife}.`);
rebalancedTrait('act6', 'buff', '☀ даёт 2 чипсины вместо 1',
  '☀: если ты контролируешь хотя бы 2 сокровища и/или твари, получи 2 чипсины.');
rebalancedTrait('act10', 'buff', '☀ также даёт 1 чипсину',
  '☀: если ты контролируешь хотя бы 2 волшебников и/или заклинания, получи 1 чипсину, затем сыграй и сбрось верхнюю карту колоды выбранного врага. Считается, что ты контролируешь эту карту во время этого хода.');
rebalancedTrait('beastTop', 'buff', 'за полученную тварь — 1 чипсина',
  'Когда ты получаешь тварь, получи 1 чипсину и можешь положить её на верх своей колоды.');
rebalancedTrait('wizardChip', 'buff', 'полученного волшебника можно класть на верх колоды',
  'Получив волшебника, получи 1 чипсину и можешь положить его на верх своей колоды. Если ты лошара, можешь снова стать нормальным.');
rebalancedTrait('hrenalochka', 'buff', 'атаки палочек наносят +2 урона вместо +1',
  'Замени 1 из своих начальных карт знаков на палочку-хреналочку. Атаки твоих палочек наносят +2 урона, и их нельзя избежать.');
rebalancedTrait('permChip', 'buff', 'за сыгранную постоянку 2 чипсины вместо 1',
  'Каждый раз, когда ты играешь карту-постоянку, получай 2 чипсины. Каждый раз, когда ты получаешь карту-постоянку, можешь класть её на верх своей колоды.');

// ---------- ПАССИВНЫЕ СВОЙСТВА ЖЕТОНОВ ДОХЛЫХ КОЛДУНОВ ----------
export const DDK_PASSIVES: Record<string, DdkPassive> = {
  // «если у любого колдуна есть второй жетон с таким текстом — уничтожь оба»
  k01: { destroyedAtEnd(G, _p, self) { return G.players.reduce((n, x) => n + x.ddk.filter((id) => ddkDef(id).key === self.key).length, 0) >= 2; } },
  // штраф за вялые палочки ×2 (при Виагрусе штрафа нет — удваивать нечего)
  k02: { endGameVP(_G, _p, c) { return c.limpCount && !c.limpPositive ? { label: 'ЖДК: штраф за вялые палочки ×2', value: RULES.limpPenalty * c.limpCount } : null; } },
  k12: { endGameVP(_G, p) { return p.loser ? { label: 'ЖДК: штраф лошары ×2', value: RULES.loserPenalty } : null; } },
  k16: {
    activation: {
      id: 'ddk-k16', label: 'ЖДК: уничтожить жетон за 5 чипсин', hint: 'Нужно 5 чипсин',
      condition: (_G, p) => p.chips >= 5,
      *run(G, p) {
        spendChips(G, p, 5, 'ЖДК: уничтожение жетона');
        const k16 = p.ddk.find((id) => ddkDef(id).key === 'k16');
        if (k16) removeDdk(G, p, k16);
      },
    },
  },
  k20: { modifyDamage(_G, c, _t, n) { return c.card && isWand(c.card) ? n + 4 : n; } },
  k23: { noPrizeBonus: true },
};

// ---------- БЕСПРЕДЕЛЫ ----------
export function* playBespredel(G: Game, active: Player, c: CardInst, source: Player | null = null): Eff {
  const fx = BESPREDEL[c.cid];
  if (!fx) throw new Error(`Беспредел «${card(c).name}» не заскриптован`);
  yield* fx(G, active, { source, card: c, bespredel: true, isAttack: true, text: card(c).text });
}

function* bespredelAttackAll(G: Game, active: Player, bctx: EffectCtx, onHit: E.OnHit): Eff<Player[]> {
  return yield* E.attack(G, bctx, { targets: clockwiseFrom(G, active), bespredel: true, onHit });
}

export const BESPREDEL: Record<CardId, BespredelEffect> = {
  // ---- мега ----
  'megabespredel-vyalye-palochki': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      for (let i = 0; i < 3; i++) gainLimp(G, t, 'hand');
    });
  },
  'megabespredel-loshara': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      if (t.loser) yield* E.stopLoser(G, t);
      else yield* E.becomeLoser(G, t);
    });
  },
  'megabespredel-unichtozhit-kartu': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      if (t.loser) {
        yield* E.trashFromHandOrDiscard(G, t, { max: 1, title: 'Мега беспредел: можешь уничтожить 1 карту (рука/сброс)' });
        continue;
      }
      // не лошара: можно и то и другое — 1 с руки и 1 из сброса
      yield* E.trashFrom(G, t, 'hand', t.hand, { min: 0, max: 1, title: 'Мега беспредел: можешь уничтожить 1 карту с руки' });
      yield* E.trashFrom(G, t, 'discard', t.discard, { min: 0, max: 1, title: 'Мега беспредел: можешь уничтожить 1 карту из сброса' });
    }
  },
  'megabespredel-dorogaya-legenda': function* (G, a, b) {
    const n = G.legendMarket.length ? Math.max(...G.legendMarket.map(costOf)) : 0;
    yield* bespredelAttackAll(G, a, b, function* (t) {
      yield* E.damage(G, b, t, n);
    });
  },
  'megabespredel-unichtozhaet-verhnyuyu-kartu': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      const c = G.mainDeck.pop();
      if (!c) return;
      log(G, `${t.name} уничтожает «${card(c).name}» из основной колоды`);
      trashCard(G, c);
      if (card(c).type === 'беспредел') {
        t.life = 0;
        yield* E.die(G, b, t);
      }
    });
  },
  'megabespredel-chipsina-za-zheton': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) gainChips(G, t, ddkCount(G, t), 'мегабеспредел');
  },
  'megabespredel-zhizni-k-5': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      // «свести к 5» — привести к значению: и вниз, и вверх (принятая трактовка)
      setLife(G, t, 5);
    });
  },
  // ---- обычные ----
  'bespredel-sbrosit-ili-otkhvatit': function* (G, a, b) {
    for (const t of clockwiseFrom(G, a)) {
      const ans = yield* E.chooseOption(G, t, [{ id: 'discard', label: 'Сбросить руку и взять 5 карт' }, { id: 'dmg', label: 'Отхватить 5 урона' }], 'Беспредел: выбери', { cards: t.hand.map(E.cardRef) });
      if (ans === 'discard') {
        discardCards(G, t, t.hand);
        drawCards(G, t, 5);
      } else yield* E.damage(G, b, t, 5);
    }
  },
  'bespredel-unichtozhit-obe': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      const cs: CardInst[] = [];
      for (let i = 0; i < 2; i++) {
        const c = revealTop(G, t);
        if (!c) break;
        t.deck.pop();
        cs.push(c);
      }
      if (!cs.length) continue;
      const yes = yield* E.confirm(G, t, 'Беспредел: 2 верхние карты твоей колоды — уничтожить обе?', 'Уничтожить обе', 'Оставить в сбросе', { cards: cs.map(E.cardRef) });
      if (yes) for (const c of cs) trashCard(G, c, t);
      else discardCards(G, t, cs);
    }
  },
  'bespredel-kto-kruche': function* (G, a) {
    const parts: Player[] = [];
    for (const t of clockwiseFrom(G, a)) {
      const sum = t.hand.reduce((acc, c) => acc + costOf(c), 0);
      const yes = yield* E.confirm(G, t, `Баттл «Кто круче»: участвовать? Стоимость карт у тебя на руке: ${sum}`, 'Участвую', 'Пас', { cards: t.hand.map(E.cardRef) });
      if (yes) parts.push(t);
    }
    if (!parts.length) return;
    const sums = parts.map((t) => ({ t, s: t.hand.reduce((acc, c) => acc + costOf(c), 0) }));
    const best = Math.max(...sums.map((x) => x.s));
    yield* E.info(G, null, 'Результаты баттла', sums.map((x) => `${x.t.name}: ${x.s} (${x.t.hand.map((c) => card(c).name).join(', ') || 'пусто'})`).join('\n'), { cards: parts.flatMap((t) => t.hand.map(E.cardRef)) });
    for (const x of sums) {
      if (x.s === best) drawCards(G, x.t, 2);
      else discardCards(G, x.t, x.t.hand);
    }
  },
  'bespredel-loshara-normalnym': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      if (!t.loser) continue;
      const opts: PromptOption[] = [{ id: 'no', label: 'Остаться лошарой' }];
      if (t.life > 5) opts.unshift({ id: 'life', label: 'Потратить 5 жизней' });
      if (t.chips >= 1) opts.unshift({ id: 'chip', label: 'Потратить 1 чипсину' });
      const ans = yield* E.chooseOption(G, t, opts, 'Беспредел: стать нормальным?');
      if (ans === 'life') {
        t.life -= 5;
        yield* E.stopLoser(G, t);
      } else if (ans === 'chip') {
        spendChips(G, t, 1, 'беспредел: стать нормальным');
        yield* E.stopLoser(G, t);
      }
    }
  },
  'bespredel-sbros-postoyanok': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      const n = Math.ceil(t.permanents.length / 2);
      if (!n) return;
      const cs = yield* E.chooseCards(G, t, t.permanents, { min: n, max: n, title: `Беспредел: сбрось ${n} из ${t.permanents.length} постоянок (останется ${t.permanents.length - n})`, kind: 'discard' });
      discardCards(G, t, cs);
    });
  },
  'bespredel-uron-dorogaya-karta': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      const n = t.hand.length ? Math.max(...t.hand.map(costOf)) : 0;
      yield* E.damage(G, b, t, n);
    });
  },
  'bespredel-legendarnaya-barakholka': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) gainChips(G, t, 1, 'беспредел');
    // «на замену» — столько же, сколько уничтожено (барахолка могла быть расширена до 4)
    yield* wipeLegendMarket(G, a, Math.max(legendMarketSizeOf(G), G.legendMarket.length));
  },
  'bespredel-legendarnaya-baraholka': function* (G, a) {
    yield* wipeLegendMarket(G, a, 4);
  },
  'bespredel-2-chipsiny-uron': function* (G, a, b) {
    for (const t of clockwiseFrom(G, a)) gainChips(G, t, 2, 'беспредел');
    yield* bespredelAttackAll(G, a, b, function* (t) {
      yield* E.damage(G, b, t, t.chips);
    });
  },
  'bespredel-sluchaynaya-karta': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      while (t.hand.length) {
        const c = t.hand[randInt(rngOf(G), t.hand.length)];
        if (!c) break;
        const opts: PromptOption[] = [{ id: 'trash', label: `Уничтожить «${card(c).name}»` }];
        if (t.life > 2) opts.push({ id: 'reroll', label: 'Потратить 2 жизни и раскрыть другую' });
        const ans = yield* E.chooseOption(G, t, opts, `Беспредел: раскрыта «${card(c).name}»`);
        if (ans === 'trash') {
          removeFrom(t.hand, c.iid);
          trashCard(G, c, t);
          break;
        }
        t.life -= 2;
        log(G, `${t.name} тратит 2 жизни`);
      }
    }
  },
  'bespredel-unichtozh-kartu': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      if (t.loser) {
        if (t.life <= 3) continue;
        const yes = yield* E.confirm(G, t, 'Потратить 3 жизни, чтобы уничтожить карту?', 'Да', 'Нет', { cards: [...t.hand, ...t.discard].map(E.cardRef) });
        if (!yes) continue;
        const cs = yield* E.trashFromHandOrDiscard(G, t, { max: 1 });
        if (cs.length) t.life -= 3;
      } else yield* E.trashFromHandOrDiscard(G, t, { max: 1 });
    }
  },
  'bespredel-chipsina-na-baraholku': function* (G) {
    for (const c of G.market) addMarketChip(G, c);
  },
  'bespredel-sbros-kolody': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      discardCards(G, t, t.deck, `всю колоду (${t.deck.length})`);
      yield* E.trashFrom(G, t, 'discard', t.discard, { min: 1, max: 1, title: 'Уничтожь 1 карту из сброса' });
    }
  },
  'bespredel-hilyy-loshara': function* (G, a, b) {
    const m = Math.min(...G.players.map((x) => x.life));
    const cands = G.players.filter((x) => x.life === m);
    let t = cands[0] ?? null;
    if (cands.length > 1) t = yield* E.choosePlayer(G, a, cands, 'Кто самый хилый (ничья — выбирает активный)?');
    if (!t) return;
    yield* E.attack(G, b, {
      targets: [t], bespredel: true, onHit: function* (x) {
        yield* E.becomeLoser(G, x);
        E.setLifeTo(G, x, maxLife(x), 'к максимуму');
      },
    });
  },
  'bespredel-5-urona': function* (G, a, b) {
    yield* bespredelAttackAll(G, a, b, function* (t) {
      yield* E.damage(G, b, t, 5);
    });
  },
  'bespredel-chipsina-krome-loshar': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) if (!t.loser) gainChips(G, t, 1, 'беспредел');
  },
  'bespredel-vrag-chipsina': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      const e = yield* E.pickEnemy(G, t, 'Беспредел: выбери врага, он получит 1 чипсину');
      if (e) gainChips(G, e, 1, 'беспредел');
    }
  },
  'bespredel-zhizni-10': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      // «свести к 10» — привести к значению в любую сторону (принятая трактовка): при 8 жизнях — +2 и чипсина
      if (!G.chipsSupply) continue;
      const yes = yield* E.confirm(G, t, t.life === 10 ? 'Жизни уже 10 — получить 1 чипсину?' : `Свести жизни с ${t.life} к 10 ради 1 чипсины?`);
      if (yes) {
        setLife(G, t, 10);
        gainChips(G, t, 1, 'беспредел');
      }
    }
  },
  'bespredel-polovina-chipsin': function* (G, a) {
    for (const t of clockwiseFrom(G, a)) {
      // без чипсин терять нечего — свойство недоступно (как с жизнями: платить можно только тем, что есть);
      // с 1 чипсиной половина «округляя вниз» — 0: уничтожить карту можно бесплатно
      if (t.chips < 1) {
        E.note(G, t, 'нет чипсин — терять нечего, уничтожить карту нельзя');
        continue;
      }
      const half = Math.floor(t.chips / 2);
      if (![...t.hand, ...t.discard].length) continue;
      const yes = yield* E.confirm(G, t, half ? `Потерять ${half} чипсин, чтобы уничтожить карту?` : 'Уничтожить карту? (половина от 1 чипсины, округляя вниз, — 0)', 'Да', 'Нет', { cards: [...t.hand, ...t.discard].map(E.cardRef) });
      if (yes) {
        spendChips(G, t, half, 'беспредел: за уничтожение карты');
        yield* E.trashFromHandOrDiscard(G, t, { min: 1, max: 1, title: 'Уничтожь 1 карту (рука/сброс)' });
      }
    }
  },
  'bespredel-golosovanie': function* (G, a) {
    const votes = new Map<number, number>();
    for (const t of clockwiseFrom(G, a)) {
      const v = yield* E.pickWizard(G, t, 'Голосуй: кто станет лошарой?', { harm: true });
      if (v) votes.set(v.id, (votes.get(v.id) ?? 0) + 1);
    }
    if (!votes.size) return;
    const best = Math.max(...votes.values());
    for (const pid of [...votes.keys()].sort((a, b) => a - b)) if (votes.get(pid) === best) yield* E.becomeLoser(G, player(G, pid));
  },
};

/** Уничтожить легендарную барахолку и выложить новые карты (мегабеспределы применяются) */
function* wipeLegendMarket(G: Game, active: Player, n: number): Eff {
  for (const c of G.legendMarket) trashCard(G, c);
  G.legendMarket = [];
  while (G.legendMarket.length < n) {
    const c = G.legendDeck.pop();
    if (!c) break;
    if (card(c).type === 'мегабеспредел') {
      log(G, `МЕГА БЕСПРЕДЕЛ! ${card(c).text}`);
      yield* E.info(G, null, 'МЕГА БЕСПРЕДЕЛ!', card(c).text, { cards: [E.cardRef(c)] });
      yield* playBespredel(G, active, c);
      trashCard(G, c);
      continue;
    }
    G.legendMarket.push(c);
    placeMarketChip(G, c);
  }
}

// ---------- ЗАЩИТА ----------
function discardDef(G: Game, p: Player, dc: CardInst): void {
  removeFrom(p.hand, dc.iid);
  p.discard.push(dc);
  E.emit(G, { type: 'discard', pid: p.id, iids: [dc.iid], cids: [dc.cid] });
}
/** Карты защиты, которые нельзя сыграть без ресурсов — не предлагаются (иначе «сгорала» бы единственная защита) */
export const DEFENSE_CAN: Record<CardId, Condition> = {
  'stakanchik-iz-tsarbaksa': (_G, p) => p.chips >= 1 && p.life > 1,
  'nikchemuhazhery': (_G, p) => p.hand.length > 1,
};
/** Обычная защита: сбросить карту защиты */
export const DEFAULT_DEFENSE: DefenseEffect = function* (G, _c, p, dc) {
  discardDef(G, p, dc);
  return true;
};
export const DEFENSE_EFFECTS: Record<CardId, DefenseEffect> = {
  'berserk-shchelkunchik': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); return true; },
  'besopanki': function* (G, c, p, dc) {
    discardDef(G, p, dc);
    drawCards(G, p, 1);
    if (c.source) yield* E.damage(G, { source: p, card: dc, isAttack: false }, c.source, 3);
    return true;
  },
  'bliznec-bubenec': function* (G, _c, p, dc) {
    discardDef(G, p, dc);
    const beasts = p.discard.filter((x) => card(x).type === 'тварь' && x.iid !== dc.iid);
    const [b] = yield* E.chooseCards(G, p, beasts, { min: beasts.length ? 1 : 0, max: 1, title: 'Возьми тварь из сброса на руку', zoneLabel: 'сброс' });
    if (b) moveCards(G, p, [b], 'hand');
    return true;
  },
  'lampovoe-zhelanie': function* (G, _c, p, dc) {
    moveCards(G, p, [dc], 'deckTop');
    return true;
  },
  'stakanchik-iz-tsarbaksa': function* (G, _c, p) {
    if (p.life <= 1 || p.chips < 1) {
      yield* E.info(G, p, 'Стаканчик', 'Нужны 1 жизнь и 1 чипсина.');
      return false;
    }
    p.life -= 1;
    spendChips(G, p, 1, 'Стаканчик из Царбакса');
    log(G, `${p.name} тратит 1 жизнь (Стаканчик)`);
    return true;
  },
  'sherif-muhtar': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 2); return true; },
  'glistomag': function* (G, _c, p, dc) {
    discardDef(G, p, dc);
    yield* E.trashFrom(G, p, 'hand', p.hand, { min: 0, max: 1, title: 'Можешь уничтожить 1 карту с руки' });
    return true;
  },
  'idol-schastlivoj-suchki': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); gainChips(G, p, 1, card(dc).name); return true; },
  // ребаланс: 1 чипсина вместо 2 — дешёвая вечная защита с прибылью была сильнее цены (волна 2 лаборатории)
  'zolotoj-parashyut': function* (G, _c, p, dc) { discardDef(G, p, dc); gainChips(G, p, reb(G, 2) ? 1 : 2, card(dc).name); return true; },
  'legendohranitel': function* (G, _c, p, dc) { const n = reb(G, 9) ? 1 : 2; discardDef(G, p, dc); drawCards(G, p, n); gainChips(G, p, n, card(dc).name); return true; },
  'bratya-loshashnye': function* (G, c, p, dc) {
    discardDef(G, p, dc);
    drawCards(G, p, 3);
    if (c.source && c.source.loser) return { redirect: true };
    return true;
  },
  'malchik-kotoryj-vyzhral': function* (G, _c, p, dc) {
    discardDef(G, p, dc);
    const wands = p.discard.filter((x) => isWand(x));
    if (wands.length) {
      const yes = yield* E.confirm(G, p, `Взять все палочки из сброса на руку (${wands.length})?`);
      if (yes) moveCards(G, p, wands, 'hand');
    }
    return true;
  },
  'poehavshij-notari-us': function* (G, _c, p, dc) {
    discardDef(G, p, dc);
    const top = G.mainDeck[G.mainDeck.length - 1];
    if (top) {
      if (!isBespredel(top)) {
        const yes = yield* E.confirm(G, p, 'Верхняя карта основной колоды — взять на руку?', 'Взять', 'Оставить', { cards: [E.cardRef(top)] });
        if (yes) {
          G.mainDeck.pop();
          gainCard(G, p, top, 'hand');
        }
      } else yield* E.info(G, p, 'Верхняя карта основной колоды', 'Беспредел — взять нельзя.', { cards: [E.cardRef(top)] });
    }
    return true;
  },
  'zozhlozhniki': function* (G, c, p, dc) {
    discardDef(G, p, dc);
    drawCards(G, p, 1);
    if (c.source) yield* E.damage(G, { source: p, card: dc, isAttack: false }, c.source, 6);
    return true;
  },
  'muertito-knizhnyj-cherv': function* (G, c, p, dc) {
    discardDef(G, p, dc);
    drawCards(G, p, 1);
    const a = c.source;
    if (a && p.ddk.length && a.ddk.length) {
      const yes = yield* E.confirm(G, p, 'Обменять 1 свой ЖДК на 1 ЖДК атакующего (эффекты обоих применяются)?');
      if (yes) {
        const mine = yield* E.chooseOption(G, p, p.ddk.map((id) => ({ id, label: E.ddkLabel(id) })), 'Какой свой жетон отдать?');
        const his = yield* E.chooseOption(G, p, a.ddk.map((id) => ({ id, label: E.ddkLabel(id) })), 'Какой жетон атакующего забрать?');
        removeDdk(G, p, mine, a);
        removeDdk(G, a, his, p);
        yield* E.applyDdk(G, { source: null }, a, mine, { from: p });
        yield* E.applyDdk(G, { source: null }, p, his, { from: a });
      }
    }
    return true;
  },
  'benzopila-druzhok': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); return { redirect: true }; },
  'myzhsemya': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 2); return true; },
  'schastlivyj-viabu': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); return true; },
  'konduktor-zhmi-na-tormoza': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); return { redirect: true }; },
  'salnyj-shut': function* (G, _c, p, dc) { discardDef(G, p, dc); drawCards(G, p, 1); return { redirect: true }; },
  'nikchemuhazhery': function* (G, _c, p, dc) {
    const others = p.hand.filter((x) => x.iid !== dc.iid);
    if (!others.length) return false;
    const r = others[randInt(rngOf(G), others.length)];
    if (!r) return false;
    log(G, `${p.name} раскрывает Никчемухажёров`);
    discardCards(G, p, [r]);
    return true;
  },
};
// правка режима ребаланса в защите выше (ветка reb(G, версия))
rebalanced('zolotoj-parashyut', 'nerf', 'защита: после сброса 1 чипсина вместо 2',
  '+1 мощь\nЗащита: можешь сбросить эту карту, чтобы избежать атаки. Если сбросил, получи 1 чипсину.');

// ---------- АКТИВАЦИИ ☀ ----------
/**
 * Активации, которые срабатывают сами, как только условие выполнено: чистое получение ресурса, без решений
 * и без выбора момента. Жрангутан/Развратот/Лохолох зависят от момента (сколько тварей, сколько мощи), а Архимаг,
 * Отходы, Баклажаба, Трондец требуют выбора — они остаются ручными.
 */
export const AUTO_ACTIVATE: ReadonlySet<CardId> = new Set<CardId>(['sukkulyondra']);
/**
 * Ситуативные активации — карта уничтожает сама себя ради разового эффекта (Архимаг, Отходы, Баклажаба):
 * это не «неприменённое свойство», а запасной ход. UI не считает их в бейдже ☀ и не подсвечивает.
 */
export const SITUATIONAL_ACTIVATE: ReadonlySet<CardId> = new Set<CardId>(['marmeladnyy-arkhimag', 'volshebnye-otkhody', 'baklazhaba']);
/** Условия активации ☀: пока не выполнены — кнопка недоступна и ☀ не тратится */
export const CAN_ACTIVATE: Record<CardId, Condition> = {
  'spinogryzogryzy': (G, p) => countCtl(G, p, 'тварь') >= 2,
  'sukkulyondra': (G, p) => countCtl(G, p, 'тварь') >= 2,
  'ispanskij-styd': (G, p) => countCtl(G, p, 'тварь') >= 2,
  'nadyozhnyj-plan': (G, p) => controlled(G, p).length >= 11,
};
export const ACTIVATIONS: Record<CardId, Activation> = {
  'edinstvennyy-v-mire-loholoh': function* (G) {
    // удвоение — прибавка всей нынешней мощи: событие power видно в ленте и в подсчёте мощи за ход (ачивки)
    E.addPower(G, turnOf(G).power, 'Лохолох: мощь удвоена');
  },
  'spinogryzogryzy': function* (G, p) {
    if (countCtl(G, p, 'тварь') < 2) return yield* E.info(G, p, 'Спиногрызогрызы', 'Нужна ещё хотя бы 1 тварь под контролем.');
    yield* E.trashFromHandOrDiscard(G, p, { max: 1 });
  },
  'sukkulyondra': function* (G, p) {
    if (countCtl(G, p, 'тварь') < 2) return yield* E.info(G, p, 'Суккулёндра', 'Нужна ещё хотя бы 1 тварь под контролем.');
    gainChips(G, p, 1, 'Суккулёндра');
  },
  'ispanskij-styd': function* (G, p, c) {
    if (countCtl(G, p, 'тварь') < 2) return yield* E.info(G, p, 'Испанский стыд', 'Нужна ещё хотя бы 1 тварь под контролем.');
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 9);
    if (t) yield* E.attackDamage(G, cx, [t], 9);
  },
  'zhrangutan': function* (G, p) { E.addPower(G, 3 * countCtl(G, p, 'тварь')); },
  'razvratot': function* (G, p, c) {
    const n = 3 * countCtl(G, p, 'тварь');
    if (!n) {
      E.note(G, p, 'нет тварей под контролем — атаки нет');
      return;
    }
    yield* E.attackDamage(G, ctx(p, c), enemies(G, p), n);
  },
  'nadyozhnyj-plan': function* (G, p) {
    if (controlled(G, p).length < 11) return yield* E.info(G, p, 'Надёжный план', 'Нужно контролировать 11 карт или больше.');
    const top = G.legendDeck.slice(-5);
    if (!top.length) return;
    const legends = top.filter((x) => card(x).type !== 'мегабеспредел');
    if (!legends.length) {
      E.note(G, p, 'среди верхних карт колоды легенд нет легенд');
      return;
    }
    const [pick] = yield* E.chooseCards(G, p, legends, { min: 1, max: 1, title: 'Положи 1 легенду на верх своей колоды', zoneLabel: 'колода легенд' });
    if (!pick) return;
    removeFrom(G.legendDeck, pick.iid);
    moveCards(G, p, [pick], 'deckTop');
    // остальные возвращаются в любом порядке: спрашиваем, какая ляжет сверху следующей
    let rest = top.filter((x) => x.iid !== pick.iid);
    for (const x of rest) removeFrom(G.legendDeck, x.iid);
    const ordered: CardInst[] = [];
    while (rest.length > 1) {
      const [nx] = yield* E.chooseCards(G, p, rest, { min: 1, max: 1, title: `Какая карта ляжет ${ordered.length ? 'следующей' : 'самой верхней'} в колоду легенд?`, zoneLabel: 'колода легенд' });
      const chosen = nx ?? rest[0];
      if (!chosen) break;
      ordered.push(chosen);
      rest = rest.filter((x) => x.iid !== chosen.iid);
    }
    ordered.push(...rest);
    G.legendDeck.push(...ordered.reverse());
  },
  'marmeladnyy-arkhimag': function* (G, p, c) {
    removeFrom(p.permanents, c.iid);
    trashCard(G, c, p);
    if (p.loser) {
      const yes = yield* E.confirm(G, p, 'Перестать быть лошарой?');
      if (yes) yield* E.stopLoser(G, p);
    }
    E.heal(G, p, 7);
  },
  'volshebnye-otkhody': function* (G, p, c) {
    removeFrom(p.permanents, c.iid);
    trashCard(G, c, p);
    yield* E.trashFrom(G, p, 'hand', p.hand, { min: 0, max: 2, title: 'Уничтожь до 2 карт с руки' });
  },
  'baklazhaba': function* (G, p, c) {
    removeFrom(p.permanents, c.iid);
    trashCard(G, c, p);
    const t = yield* E.pickWizard(G, p, 'Кто станет лошарой?');
    if (t) yield* E.attack(G, ctx(p, c), { targets: [t], onHit: function* (x) { yield* E.becomeLoser(G, x); } });
  },
  'trondets': function* (G, p, c) {
    const others = controlled(G, p).filter((x) => x.iid !== c.iid);
    if (!others.length) return yield* E.info(G, p, 'Трондец', 'Нет других карт под контролем.');
    const [o] = yield* E.chooseCards(G, p, others, { min: 1, max: 1, title: 'Трондец: урон = стоимость какой карты?' });
    if (!o) return;
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), costOf(o));
    if (t) yield* E.attackDamage(G, cx, [t], costOf(o));
  },
};

// ---------- ЭФФЕКТЫ КАРТ ПРИ РОЗЫГРЫШЕ ----------
const noop: CardEffect = function* () { /* эффект только пассивный/в защите/при активации */ };
const dohlyak: CardEffect = function* (G, p, c) {
  // ребаланс: чипсины только за настоящие жетоны — самокорм класса (дохляки считаются жетонами и кормят друг
  // друга) держал всех пятерых на +7–10 п.п. выше цены (лаборатория: без самокорма все в норме)
  if (reb(G, 1)) gainChips(G, p, p.ddk.length, `${card(c).name}: по 1 за жетон`);
  else gainChips(G, p, ddkCount(G, p), `${card(c).name}: по 1 за жетон и дохляка`);
};
const revealAndTrashOrPower: CardEffect = function* (G, p) {
  const top = yield* revealOwnTop(G, p, { silent: true });
  if (!top) return;
  const ans = yield* E.chooseOption(G, p, [{ id: 'trash', label: 'Уничтожить' }, { id: 'power', label: `+${costOf(top)} мощи` }], 'Говна-пирога: верхняя карта колоды', { cards: [E.cardRef(top)] });
  if (ans === 'trash') trashOwnTop(G, p, top);
  else E.addPower(G, costOf(top));
};

export const CARD_EFFECTS: Record<CardId, CardEffect> = {
  // --- затравки и базовые ---
  'znak': noop, // +1 мощь — из поля power
  'pshik': noop,
  'syrnaya-palochka': function* (G, p, c) {
    const cx = ctx(p, c, { onKill: function* () { gainChips(G, p, 3, 'Сырная палочка'); } });
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 1);
    if (t) yield* E.attackDamage(G, cx, [t], 1);
  },
  'palochka-hrenalochka': function* (G, p, c) {
    const cx = ctx(p, c, {
      onKill: function* (v) {
        if (!p.discard.length) {
          E.note(G, p, 'стопка сброса пуста — передавать нечего');
          return;
        }
        const cs = yield* E.chooseCards(G, p, p.discard, { min: 0, max: 2, optional: true, title: `Передать до 2 карт из сброса на руку ${v.name}`, zoneLabel: 'сброс' });
        for (const x of cs) giveCard(G, p, v, x, 'hand');
      },
    });
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 1);
    if (t) yield* E.attackDamage(G, cx, [t], 1);
  },
  'shalnaya-magiya': function* (G, p) {
    const ans = yield* E.chooseOption(G, p, [{ id: 'power', label: '+2 мощи' }, { id: 'steal', label: 'Сыграть верхнюю карту колоды врага' }], 'Шальная магия');
    if (ans === 'power') E.addPower(G, 2);
    else {
      const e = yield* E.pickEnemy(G, p, 'Шальная магия: с чьей колоды сыграть верхнюю карту?');
      if (e) yield* E.playTopOfEnemyDeck(G, p, e);
    }
  },
  // --- легенды ---
  'ta-samaya-vyalaya-palochka': function* (G, p, c) {
    const cx = ctx(p, c, {
      onKill: function* (v) {
        // «можешь дать ему 3 или меньше вялых палочек с руки, ИЛИ из сброса, ИЛИ из колоды вялых палочек»
        const inHand = p.hand.filter((x) => card(x).type === 'вялая палочка').length;
        const inDiscard = p.discard.filter((x) => card(x).type === 'вялая палочка').length;
        const opts: PromptOption[] = [];
        if (inHand) opts.push({ id: 'hand', label: `С руки (есть ${inHand})` });
        if (inDiscard) opts.push({ id: 'discard', label: `Из сброса (есть ${inDiscard})` });
        if (G.limpPile.length) opts.push({ id: 'pile', label: `Из колоды вялых палочек (${G.limpPile.length})` });
        if (!opts.length) {
          E.note(G, p, 'вялых палочек нигде нет — дать нечего');
          return;
        }
        opts.push({ id: 'no', label: 'Не давать' });
        const ans = yield* E.chooseOption(G, p, opts, `Дать ${v.name} до 3 вялых палочек — откуда?`);
        if (ans === 'pile') {
          const n = yield* E.chooseNumber(G, p, 0, Math.min(3, G.limpPile.length), 'Сколько вялых палочек дать?');
          for (let i = 0; i < n; i++) gainLimp(G, v);
        } else if (ans === 'hand' || ans === 'discard') {
          const zone: PlayerZone = ans;
          const pool = p[zone].filter((x) => card(x).type === 'вялая палочка');
          const cs = yield* E.chooseCards(G, p, pool, { min: 0, max: 3, optional: true, title: 'Выбери до 3 вялых палочек' });
          for (const x of cs) giveCard(G, p, v, x, 'discard');
        }
      },
    });
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 7);
    if (t) yield* E.attackDamage(G, cx, [t], 7);
  },
  'malchishnik-v-rlyehe': function* (G, p, c) {
    for (const t of clockwiseFrom(G, p)) drawCards(G, t, 2);
    yield* E.attack(G, ctx(p, c), {
      targets: enemies(G, p), unavoidable: true, onHit: function* (t) {
        const cs = yield* E.discardFromHand(G, t, 2, { random: true });
        yield* E.damage(G, ctx(p, c), t, cs.reduce((a, x) => a + costOf(x), 0));
      },
    });
  },
  'zhnitsa-lyubvi': function* (G, p, c) {
    if (!p.ddk.length) return;
    const t = yield* E.pickWizard(G, p, 'Кому отдать жетон дохлого колдуна?');
    if (!t) return;
    yield* E.attack(G, ctx(p, c), {
      targets: [t], onHit: function* (v) {
        const id = yield* E.chooseOption(G, p, p.ddk.map((x) => ({ id: x, label: E.ddkLabel(x) })), 'Какой жетон дохлого колдуна отдать?');
        removeDdk(G, p, id, v);
        yield* E.applyDdk(G, { source: p }, v, id, { from: p });
      },
    });
  },
  'venerina-magolovka': function* (G, p, c) {
    const t = yield* E.pickWizard(G, p);
    if (!t || t.id === p.id) return;
    yield* E.attack(G, ctx(p, c), {
      targets: [t], onHit: function* (v) {
        const ans = yield* E.chooseOption(G, p, [
          { id: 'life', label: 'Обменяться жизнями' }, { id: 'loser', label: 'Обменяться статусом лошары' }, { id: 'both', label: 'И тем и другим' }, { id: 'no', label: 'Ничем' },
        ], 'Венерина маголовка');
        // сначала статус (от него зависит предел жизней), потом жизни. Обмен и тем и другим (с v19): жизни — те, что
        // были до обмена статусом, а потолок нового лошары — уже к полученным (иначе бывший лошара получал жизни,
        // срезанные до 15)
        const both = ans === 'both' && swapLivesBeforeCap(G);
        const [lp0, lv0] = [p.life, v.life];
        if ((ans === 'loser' || ans === 'both') && p.loser !== v.loser) {
          [p.loser, v.loser] = [v.loser, p.loser];
          for (const x of [p, v]) {
            log(G, x.loser ? `${x.name} становится лошарой (обмен)` : `${x.name} больше не лошара (обмен)`);
            emit(G, { type: 'loser', pid: x.id, on: x.loser });
            if (x.loser && !both) x.life = Math.min(x.life, maxLife(x));
          }
        }
        if (ans === 'life' || ans === 'both') {
          const [lp, lv] = both ? [lp0, lv0] : [p.life, v.life];
          E.setLifeTo(G, p, lv, 'обмен жизнями');
          E.setLifeTo(G, v, lp, 'обмен жизнями');
        }
      },
    });
  },
  'buzyashchaya-palochka-garika-potnogo': function* (G, p, c) {
    const l = leftOf(G, p);
    const r = rightOf(G, p);
    const dir = yield* E.chooseOption(G, p, [{ id: 'left', label: `Левый враг (${l.name})` }, { id: 'right', label: `Правый враг (${r.name})` }], 'Куда бить 10 урона?');
    let cur: Player = dir === 'left' ? l : r;
    let visited = 0;
    while (cur.id !== p.id && visited < G.players.length) {
      visited++;
      let killed = false;
      yield* E.attackDamage(G, ctx(p, c, { onKill: function* () { killed = true; } }), [cur], 10);
      if (!killed) break;
      cur = dir === 'left' ? leftOf(G, cur) : rightOf(G, cur);
    }
  },
  'mortal-kombo': function* (G, p, c) {
    const t = yield* E.pickEnemy(G, p, 'Кого убить?');
    if (!t) return;
    yield* E.attack(G, ctx(p, c, { ddkChoice: true }), {
      targets: [t], onHit: function* (v) {
        v.life = 0;
        yield* E.die(G, ctx(p, c, { ddkChoice: true }), v);
      },
    });
  },
  'nekrofeya-gnilyusya': function* (G, p, c) {
    // ребаланс: 5 урона за жетон вместо 4 — жетонный билд-эраунд был слабее цены (волна 2 лаборатории)
    const n = (reb(G, 2) ? 5 : 4) * ddkCount(G, p);
    if (!n) {
      E.note(G, p, 'нет жетонов дохлых колдунов — атаки нет');
      return;
    }
    yield* E.attackDamage(G, ctx(p, c), enemies(G, p), n);
  },
  'dermak-gastrit': function* (G, p, c) {
    const l = G.legendMarket[randInt(rngOf(G), G.legendMarket.length)];
    if (!l) return;
    removeFrom(G.legendMarket, l.iid);
    const chips = G.marketChips[l.iid];
    if (chips) {
      G.chipsSupply += chips;
      delete G.marketChips[l.iid];
    }
    trashCard(G, l);
    log(G, `Дерьмак Гастрит уничтожает легенду «${card(l).name}»`);
    yield* E.attackDamage(G, ctx(p, c), enemies(G, p), costOf(l));
  },
  'vosstanie-iz-zada': function* (G, p) {
    drawCards(G, p, 2);
    const e = yield* E.pickEnemy(G, p, 'Кто не сможет защищаться в этот ход?');
    if (e) turnOf(G).noDefense.push(e.id);
  },
  'sdelka-so-zhgunom': function* (G, p) {
    yield* E.trashFrom(G, p, 'discard', p.discard, { min: 0, max: 99, title: 'Уничтожь любое число карт из сброса' });
  },
  'volshebnyj-krolik': function* (G, p, c) {
    drawCards(G, p, 3);
    yield* E.attackDamage(G, ctx(p, c), enemies(G, p), 20);
  },
  'palochka-ushatalochka': function* (G, p, c) {
    const cx = ctx(p, c, { instantWin: true });
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 1);
    if (t) yield* E.attackDamage(G, cx, [t], 1);
  },
  'bratya-loshashnye': noop,
  'naryvka-koroleva-genitalnyh-garpij': function* (G, p, c) {
    yield* E.attack(G, ctx(p, c), { targets: enemies(G, p), onHit: function* (t) { gainLimp(G, t); gainLimp(G, t); } });
  },
  // ребаланс: также возьми 1 карту — снижение цены 14 → 12 не спасало (−7 к цене), добор вывел точно на кривую
  'zad-v-budushchee': function* (G, p) {
    turnOf(G).gainToHand = true;
    if (reb(G, 2)) drawCards(G, p, 1);
  },
  'hahataler-zloradnik': function* (G, p, c) {
    const t = yield* E.pickEnemy(G, p);
    if (t) yield* E.attack(G, ctx(p, c, { noRedirect: true }), {
      targets: [t], onHit: function* (v) {
        yield* E.giveDdk(G, { source: p }, v);
        yield* E.giveDdk(G, { source: p }, v);
      },
    });
  },
  'kapitan-bartolomyau': function* (G, p, c) {
    const e = yield* E.pickEnemy(G, p);
    if (!e) return;
    yield* E.attack(G, ctx(p, c), {
      targets: [e], onHit: function* (v) {
        const four: CardInst[] = [];
        for (let i = 0; i < 4; i++) {
          const x = revealTop(G, v);
          if (!x) break;
          v.deck.pop();
          four.push(x);
        }
        const playable = four.filter((x) => !isBespredel(x));
        const [pick] = yield* E.chooseCards(G, p, playable, { min: playable.length ? 1 : 0, max: 1, title: `Раскрыто: ${four.map((x) => card(x).name).join(', ')}. Сыграй 1 из них`, zoneLabel: 'колода врага' });
        for (const x of four) if (isBespredel(x)) trashCard(G, x);
        discardCards(G, v, four.filter((x) => !isBespredel(x) && x !== pick));
        if (pick) {
          p.inPlay.push(pick);
          pick.returnTo = v.id;
          yield* playCardEffect(G, p, pick, { fromEnemyDeck: true });
        }
      },
    });
  },
  'serdce-maga-po-epichnomu': function* (G, p) { drawCards(G, p, 1); gainChips(G, p, 3, 'Сердце мага'); },
  'smert-ot-holesterina': function* (G, p) {
    if (ddkCount(G, p) === 0) return E.addPower(G, 7);
    // жетоном считается и Дохляк на столе: из-за него +7 нет, но его, как жетон, можно уничтожить (с v21)
    const dohl = p.permanents.filter((c) => hasTag(c, 'deadWizard'));
    if (!p.ddk.length) E.note(G, p, `Смерть от холестерина: без +7 мощи — ${dohl.map((c) => `«${card(c).name}»`).join(', ')} считается жетоном дохлого колдуна`);
    const asTokens = holesterinDohlyak(G) ? dohl : [];
    if (!p.ddk.length && !asTokens.length) return;
    const id = yield* E.chooseOption(G, p, [
      ...p.ddk.map((x) => ({ id: x, label: `Уничтожить жетон ${E.ddkLabel(x)}` })),
      ...asTokens.map((c) => ({ id: `card:${c.iid}`, label: `Уничтожить «${card(c).name}» (считается жетоном)` })),
      { id: 'no', label: 'Не уничтожать' },
    ], 'Смерть от холестерина: какой жетон дохлого колдуна уничтожить?');
    const dc = asTokens.find((c) => id === `card:${c.iid}`);
    if (dc) {
      removeFrom(p.permanents, dc.iid);
      trashCard(G, dc, p);
    } else if (id !== 'no') removeDdk(G, p, id);
  },
  'legendohranitel': function* (G, p) { drawCards(G, p, 2); gainChips(G, p, 2, 'Легендохранитель'); },
  'palochka-miniganochka': function* (G, p, c) {
    let kills = 0;
    for (let i = 0; i < 4; i++) {
      const cx = ctx(p, c, { onKill: function* () { kills++; } });
      const t = yield* E.pickTarget(G, p, cx, [...G.players], 7, `Палочка-миниганочка, выстрел ${i + 1}/4: кому ${E.previewDamage(G, cx, p, 7)} урона?`);
      if (!t) break;
      yield* E.attackDamage(G, cx, [t], 7);
    }
    if (kills) E.addPower(G, 3 * kills);
  },
  'spinogryzogryzy': noop,
  // ребаланс: возьми 1 вместо 2 — за 16 карта держала +10 п.п. к цене, добор — главный рычаг (волна 2 лаборатории)
  'nadyozhnyj-plan': function* (G, p) { drawCards(G, p, reb(G, 2) ? 1 : 2); },
  'brataliti': function* (G) { turnOf(G).brataliti = true; },
  'orbitalnaya-evonnaya-pushka': function* (G, p, c) { yield* E.attackDamage(G, ctx(p, c), E.leftRight(G, p), 10); },
  'avada-loshavra': function* (G, p, c) {
    drawCards(G, p, 2);
    const t = yield* E.pickWizard(G, p, 'Кто станет лошарой?');
    if (t) yield* E.attack(G, ctx(p, c), { targets: [t], onHit: function* (v) { yield* E.becomeLoser(G, v); } });
    E.addPower(G, G.players.filter((x) => x.loser).length);
  },
  'viagrus-tvyordyj-lord': noop,
  'pohotlivyj-mayachok': function* (G, p) { E.addPower(G, ddkCount(G, p)); },
  'tsirk-bratev-loshashnykh': function* (G, p) { if (p.loser) E.addPower(G, 2); },
  'zhelatinovyy-sisyak': function* (G) { E.addPower(G, 1); },
  'vyalaya-bashnya': function* (G) { E.addPower(G, 1); },
  'trondets': function* (G, p, c) {
    const fx = ACTIVATIONS['trondets'];
    if (fx) yield* fx(G, p, c);
    turnOf(G).activated.push(c.iid);
  },
  // --- основная колода ---
  'priunyvshij-ork': function* (G, p) { if (countCtl(G, p, 'тварь') >= 2) E.addPower(G, 2); },
  'chleny-bandy': function* (G, p, c) { yield* E.attackDamage(G, ctx(p, c), enemies(G, p), 3); },
  'pushka-blevushka': function* (G, p, c) {
    const top = yield* revealOwnTop(G, p, { silent: true });
    if (!top) return;
    const ans = yield* E.chooseOption(G, p, [{ id: 'trash', label: 'Уничтожить её' }, { id: 'attack', label: `Атака: ${costOf(top)} урона врагу` }], 'Пушка-блевушка: верхняя карта колоды', { cards: [E.cardRef(top)] });
    if (ans === 'trash') trashOwnTop(G, p, top);
    else {
      const cx = ctx(p, c);
      const t = yield* E.pickTarget(G, p, cx, enemies(G, p), costOf(top));
      if (t) yield* E.attackDamage(G, cx, [t], costOf(top));
    }
  },
  'chyornaya-ovtsa-chashchob': function* (G, p, c) {
    const t = yield* E.pickEnemy(G, p, 'Чёрная овца чащоб: кого атаковать? (урон = стоимость верхней легенды колоды)');
    if (!t) return;
    yield* E.attack(G, ctx(p, c), {
      targets: [t], onHit: function* (v) {
        const l = G.legendDeck.pop();
        if (!l) return;
        trashCard(G, l);
        log(G, `${v.name} уничтожает «${card(l).name}» из колоды легенд`);
        yield* E.damage(G, ctx(p, c), v, costOf(l));
      },
    });
  },
  'sladkiy-smurfinyor': function* (G, p, c) {
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 4);
    if (t) yield* E.attackDamage(G, cx, [t], 4, { after: function* () { E.heal(G, p, cx.dealt ?? 0); } });
  },
  'berserk-shchelkunchik': function* (G, p, c) {
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 6);
    if (t) yield* E.attackDamage(G, cx, [t], 6);
  },
  'besopanki': function* (G, p) { drawCards(G, p, 1); },
  'povelitel-shkurok': function* (G, p, c) {
    const t = yield* E.choosePlayer(G, p, E.leftRight(G, p), 'Левый или правый враг получит вялую палочку?');
    if (t) yield* E.attack(G, ctx(p, c), { targets: [t], onHit: function* (v) { gainLimp(G, v); } });
  },
  'surkovyy-agent': function* (G, p) {
    const three = G.mainDeck.splice(-3, 3);
    const besp = three.filter((x) => card(x).type === 'беспредел');
    for (const x of three) if (!besp.includes(x)) trashCard(G, x);
    log(G, `Сурковый агент уничтожает: ${three.map((x) => card(x).name).join(', ')}`);
    if (besp.length) {
      const opts: PromptOption[] = besp.map((x) => ({ id: String(x.iid), label: card(x).text }));
      opts.push({ id: 'no', label: 'Не играть беспредел' });
      const ans = yield* E.chooseOption(G, p, opts, 'Сурковый агент: сыграть 1 беспредел и получить +3 мощи?', { cards: besp.map(E.cardRef) });
      for (const x of besp) {
        if (String(x.iid) === ans) {
          E.addPower(G, 3);
          yield* playBespredel(G, p, x, p);
        }
        trashCard(G, x);
      }
    }
  },
  'lampovoe-zhelanie': noop,
  'stakanchik-iz-tsarbaksa': noop,
  'palochka-shlyopalochka': function* (G, p, c) {
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 2);
    if (t) yield* E.attackDamage(G, cx, [t], 2, {
      after: function* (v) {
        // «укради у него или из запаса столько же чипсин, сколько урона нанёс» — выбор игрока
        const n = cx.dealt ?? 0;
        if (n <= 0) return;
        const opts: PromptOption[] = [];
        if (v.chips > 0) opts.push({ id: 'victim', label: `У ${v.name} (есть ${v.chips})` });
        if (G.chipsSupply > 0) opts.push({ id: 'supply', label: `Из запаса (${G.chipsSupply})` });
        if (!opts.length) {
          E.note(G, p, 'чипсин нет ни у жертвы, ни в запасе');
          return;
        }
        const ans = yield* E.chooseOption(G, p, opts, `Украсть ${n} чипсин: у ${v.name} или из запаса?`);
        if (ans === 'victim') {
          const k = Math.min(n, v.chips);
          v.chips -= k;
          p.chips += k;
          log(G, `${p.name} крадёт ${k} чипсин у ${v.name}`);
          E.emit(G, { type: 'chips', pid: v.id, n: -k, src: `украл ${p.name}` });
          E.emit(G, { type: 'chips', pid: p.id, n: k, src: `украдено у ${v.name}` });
        } else gainChips(G, p, n, card(c).name);
      },
    });
  },
  'bratstvo-kontsa': function* (G, p) {
    drawCards(G, p, 1);
    const o = yield* E.pickEnemy(G, p, 'Кто ещё возьмёт 1 карту?');
    if (o) drawCards(G, o, 1);
  },
  'palochka-chipsalochka': function* (G, p, c) {
    if (p.chips < 1) return;
    const yes = yield* E.confirm(G, p, 'Потратить 1 чипсину: атака 10 урона выбранному колдуну?');
    if (!yes) return;
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 10);
    if (!t) return;
    spendChips(G, p, 1, card(c).name);
    yield* E.attackDamage(G, cx, [t], 10);
  },
  'tapki-donatki': function* (G, p, c) {
    const cx = ctx(p, c);
    const n = maxCostCtl(G, p);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), n);
    if (t) yield* E.attackDamage(G, cx, [t], n);
  },
  'sosochnyy-prokolist': function* (G, p, c) { yield* E.attackDamage(G, ctx(p, c), enemies(G, p), 7); },
  'loshashlyk': function* (G, p) { gainChips(G, p, G.players.filter((x) => x.loser).length, 'Лошашлык: по 1 за лошару'); },
  // ребаланс: +1 за жетон вместо +2 (лаборатория: 38% → 27% при ожидании 28% для цены 7)
  // ребаланс: ×2, но только настоящие жетоны (как у дохляков) — режимный ×1 по всем оказался перенерфом (−6 к цене)
  'nekroshest': function* (G, p) { E.addPower(G, reb(G, 3) ? 2 * p.ddk.length : (reb(G, 1) ? 1 : 2) * ddkCount(G, p)); },
  'marmeladnyy-arkhimag': noop,
  'govna-piroga': revealAndTrashOrPower,
  'volshebnye-otkhody': noop,
  'volchiy-zhor': function* (G, p, c) {
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 4);
    if (t) yield* E.attackDamage(G, cx, [t], 4, { after: function* (v) { yield* E.discardFromHand(G, v, 1); } });
  },
  'vurdalakar': function* (G, p, c) {
    const turn = turnOf(G);
    if (turn.played.length === 1 && turn.played[0] === c.iid) {
      const yes = yield* E.confirm(G, p, 'Вурдалакар: сбросить руку и взять 4 карты?');
      if (yes) {
        discardCards(G, p, p.hand);
        drawCards(G, p, 4);
      }
    }
  },
  'shlem-totem': function* (G, p) {
    if (p.chips < 1) return;
    const yes = yield* E.confirm(G, p, 'Потратить 1 чипсину, чтобы уничтожить карту с руки/из сброса?');
    if (!yes) return;
    const cs = yield* E.trashFromHandOrDiscard(G, p, { max: 1 });
    if (cs.length) spendChips(G, p, 1, 'Шлем-тотем');
  },
  'nechistaya-sila': function* (G, p, c) { drawCards(G, p, 1); yield* E.attackDamage(G, ctx(p, c), E.leftRight(G, p), 7); },
  'mozgoshtorm': function* (G, p) { drawCards(G, p, 2); },
  'zak-dohlyak': dohlyak, 'chak-dohlyak': dohlyak, 'zhak-dohlyak': dohlyak, 'ermak-dohlyak': dohlyak, 'isaak-dohlyak': dohlyak,
  'musornyj-veter': function* (G, p, c) {
    yield* E.trashFrom(G, p, 'discard', p.discard, { min: 0, max: 1, title: 'Можешь уничтожить 1 карту из сброса' });
    yield* E.attackDamage(G, ctx(p, c), enemies(G, p), 5);
  },
  'zhidkaya-kara': function* (G, p, c) {
    const cx = ctx(p, c);
    const t = yield* E.pickTarget(G, p, cx, enemies(G, p), 7);
    if (!t) return;
    const hit = yield* E.attackDamage(G, cx, [t], 7);
    if (!hit.includes(t)) drawCards(G, p, 1);
  },
  'sherif-muhtar': function* (G, p) { drawCards(G, p, 2); },
  'glistomag': noop,
  'sukkulyondra': noop,
  'idol-schastlivoj-suchki': noop,
  'zolotoj-parashyut': noop,
  'vedmin-podarochek': function* (G, p) { drawCards(G, p, 1); E.addPower(G, p.permanents.length); },
  'dlan-tvortsa': noop,
  'baklazhaba': noop,
  'smazochnaya-gryaznaya-palka': noop,
  'suvenirnyj-laryok-v-zale-legend': noop,
  'koldunator': noop,
  'potnyj-gikpig': noop,
  'zhrangutan': noop,
  'razvratot': noop,
  'ispanskij-styd': noop,
  'epichnyj-merch-boevykh-magov': function* (G) { turnOf(G).legendDiscount += 2; },
  'palochka-losharochka': function* (G, p, c) {
    const cx = ctx(p, c, { onKill: function* (v) { yield* E.becomeLoser(G, v); } });
    const t = yield* E.pickTarget(G, p, cx, [...G.players], 5);
    if (t) yield* E.attackDamage(G, cx, [t], 5);
  },
  'pivohranilishche': noop,
  'zloshirak': noop,
  'bliznec-bubenec': noop,
  'gusynya-koroleva-mat': noop,
  'edinstvennyy-v-mire-loholoh': noop,
  'bashnya-radosti': noop,
  'ultimativnoe-tronado': noop,
  'park-vurdalakcionov': noop,
  'chipsihoz-arena': noop,
  // --- фамильяры ---
  // ребаланс: +1 мощь сверх печатных +3 — снижение цены не помогало, рычаг в эффекте (волна 3)
  'malchik-kotoryj-vyzhral': function* (G) { if (reb(G, 3)) E.addPower(G, 1, 'Мальчик, который выжрал'); },
  'poehavshij-notari-us': noop,
  'zozhlozhniki': function* (G, p) {
    const t = yield* E.pickEnemy(G, p, 'Кто сбросит случайную карту?');
    if (t) yield* E.discardFromHand(G, t, 1, { random: true });
  },
  'muertito-knizhnyj-cherv': function* (G, p) { drawCards(G, p, 1); E.addPower(G, ddkCount(G, p)); },
  'benzopila-druzhok': function* (G) { turnOf(G).nextUnavoidable = true; },
  'myzhsemya': function* (G, p) { drawCards(G, p, 1); turnOf(G).legendDiscount += 2; },
  'schastlivyj-viabu': function* (G, p, c) {
    if (!controlled(G, p).some((x) => H.isLegendFor(p, x))) return;
    const en = enemies(G, p);
    let left = 8;
    const plan: { e: Player; n: number }[] = [];
    for (let i = 0; i < en.length && left > 0; i++) {
      const e = en[i];
      if (!e) break;
      const n = i === en.length - 1 ? left : yield* E.chooseNumber(G, p, 0, left, `Сколько из ${left} урона нанести ${e.name}?`);
      plan.push({ e, n });
      left -= n;
    }
    yield* E.attackDamage(G, ctx(p, c), plan.filter((x) => x.n > 0).map((x) => x.e), (t) => plan.find((x) => x.e.id === t.id)?.n ?? 0);
  },
  'konduktor-zhmi-na-tormoza': function* (G, p) { drawCards(G, p, 2); },
  'salnyj-shut': function* (G, p, c) {
    const t = yield* E.pickEnemy(G, p);
    if (t) yield* E.attack(G, ctx(p, c), { targets: [t], onHit: function* (v) { gainLimp(G, v); } });
  },
  'nikchemuhazhery': function* (G, p) { drawCards(G, p, 1); },
};
// правки режима ребаланса в эффектах выше (ветки reb(G, версия)) и их описания для UI — в одном месте
for (const id of ['zak-dohlyak', 'chak-dohlyak', 'zhak-dohlyak', 'ermak-dohlyak', 'isaak-dohlyak'] as const) {
  rebalanced(id, 'rework', 'чипсины только за настоящие жетоны (дохляки не в счёт)',
    'Сыграв эту карту, получи 1 чипсину за каждый свой жетон дохлого колдуна (карты-дохляки не считаются).\nПостоянка: эта карта считается жетоном дохлого колдуна.');
}
rebalanced('nekroshest', 'rework', '+2 мощи только за настоящие жетоны (дохляки не в счёт)',
  '+2 мощи за каждый жетон дохлого колдуна под твоим контролем (карты-дохляки не считаются).');
rebalanced('nadyozhnyj-plan', 'nerf', 'возьми 1 карту вместо 2 (активация ☀ как есть)',
  'Возьми 1 карту.\n☀: если ты контролируешь 11 карт или больше, посмотри 5 верхних карт колоды легенд. Положи 1 из них на верх своей колоды, а остальные верни обратно в любом порядке.');
rebalanced('zad-v-budushchee', 'buff', 'также возьми 1 карту',
  '+1 мощь\nВозьми 1 карту.\nПолучая карты с барахолки во время этого хода, можешь брать их на руку.');
rebalanced('nekrofeya-gnilyusya', 'buff', '5 урона за жетон вместо 4',
  '+4 мощи\nАтака: нанеси 5 урона каждому врагу за каждый жетон дохлого колдуна под твоим контролем.');
rebalanced('legendohranitel', 'nerf', 'защита даёт 1 карту и 1 чипсину вместо 2 и 2',
  'Возьми 2 карты и получи 2 чипсины.\nЗащита: можешь сбросить эту карту, чтобы избежать атаки. Если сбросил, возьми 1 карту и получи 1 чипсину.');
rebalanced('malchik-kotoryj-vyzhral', 'buff', '+4 мощи вместо +3',
  '+4 мощи\nЗащита: можешь сбросить эту карту, чтобы избежать атаки. Если сбросил, можешь взять все «Палочки» из своей стопки сброса на руку.');

// ---------- ЖЕТОНЫ ДОХЛЫХ КОЛДУНОВ (эффект при получении) ----------
export const DDK_EFFECTS: Record<string, DdkEffect> = {
  k03: function* (G, _c, p) {
    for (const e of enemies(G, p)) {
      const signs = [...e.hand, ...e.discard].filter((x) => x.cid === STARTER_DECK.sign);
      if (!signs.length) continue;
      const [s] = yield* E.chooseCards(G, e, signs, { min: 0, max: 1, optional: true, title: `Передать ${p.name} на руку знак? (можно из сброса)`, zoneLabel: 'рука/сброс' });
      if (s) giveCard(G, e, p, s, 'hand');
    }
  },
  k04: function* (G, _c, p) { if (p.loser) yield* E.stopLoser(G, p); else yield* E.becomeLoser(G, p); },
  k05: function* (G, _c, p) {
    const top = yield* revealOwnTop(G, p, { silent: true });
    if (top) {
      const yes = yield* E.confirm(G, p, 'Верхняя карта колоды — уничтожить?', 'Уничтожить', 'Оставить', { cards: [E.cardRef(top)] });
      if (yes) trashOwnTop(G, p, top);
    }
  },
  k06: function* (G, _c, p) {
    const t = yield* E.pickEnemy(G, p, 'С кем поменяться жизнями?');
    if (t) {
      const [lp, lt] = [p.life, t.life];
      E.setLifeTo(G, p, lt, 'обмен жизнями');
      E.setLifeTo(G, t, lp, 'обмен жизнями');
    }
  },
  k07: function* (G, _c, p) { moveCards(G, p, p.permanents, 'deck'); shuffle(rngOf(G), p.deck); },
  k08: function* (G, _c, p) { gainLimp(G, p, 'deckTop'); },
  k09: function* (G, _c, p) {
    let n = 0;
    for (const e of enemies(G, p)) {
      const yes = yield* E.confirm(G, e, `Показать ${p.name} средний палец? 🖕`);
      if (yes) n++;
    }
    if (n) yield* E.discardFromHand(G, p, n);
  },
  k10: function* (G, c) {
    if (c.killer && c.killer.loser) {
      const yes = yield* E.confirm(G, c.killer, 'Перестать быть лошарой?');
      if (yes) yield* E.stopLoser(G, c.killer);
    }
  },
  k11: function* (G, _c, p) {
    const n = (yield* legendsByChoice(G, p, p.discard, 'Жетон: 1 вялая палочка за легенду в сбросе. Каких фамильяров считать легендами?', 'стопка сброса')).length;
    for (let i = 0; i < n; i++) gainLimp(G, p);
  },
  k12: function* (G, _c, p) { yield* E.becomeLoser(G, p); },
  k13: function* (G, _c, p) {
    for (let i = 0; i < 2; i++) {
      if (!gainLimp(G, p, 'deckBottom')) break;
    }
  },
  k14: function* (G, _c, p) {
    const ls = yield* legendsByChoice(G, p, p.hand, 'Жетон: легенды с руки замешиваются в колоду. Каких фамильяров считать легендами?', 'рука');
    moveCards(G, p, ls, 'deck');
    if (ls.length) shuffle(rngOf(G), p.deck);
  },
  k15: function* (G, c, p) { if (p.loser) yield* E.giveDdk(G, c, p); else yield* E.becomeLoser(G, p); },
  k17: function* (G, _c, p) { spendChips(G, p, Math.ceil(p.chips / 2), 'жетон дохлого колдуна'); },
  k18: function* (G, c, p) {
    const top = G.mainDeck[G.mainDeck.length - 1];
    if (!top) return;
    yield* E.info(G, p, 'Верхняя карта основной колоды', card(top).type === 'беспредел' ? 'Беспредел — получаешь жетон дохлого колдуна.' : 'Не беспредел — повезло.', { cards: [E.cardRef(top)] });
    if (card(top).type === 'беспредел') {
      G.mainDeck.pop();
      trashCard(G, top);
      yield* E.giveDdk(G, c, p);
    }
  },
  k21: function* (G, _c, p) { yield* E.damage(G, { source: null, isAttack: false }, p, p.chips); },
  k22: function* (G, _c, p) { gainChips(G, p, 1, 'жетон дохлого колдуна'); },
  k24: function* (G, c, p) {
    const top = yield* revealOwnTop(G, p, { silent: true });
    if (!top) return;
    const legend = (yield* legendsByChoice(G, p, [top], `Жетон: если верхняя карта — легенда, получишь ещё один жетон дохлого колдуна. Считать «${card(top).name}» легендой?`, 'верх колоды')).length > 0;
    yield* E.info(G, p, 'Верхняя карта колоды', legend ? 'Легенда — получаешь ещё один жетон дохлого колдуна.' : 'Не легенда — пронесло.', { cards: [E.cardRef(top)] });
    if (legend) yield* E.giveDdk(G, c, p);
  },
  k25: function* (G, c) { if (c.killer) gainChips(G, c.killer, 2, 'жетон дохлого колдуна'); },
  k26: function* (G, _c, p) {
    yield* E.revealHand(G, p);
    const n = p.hand.length ? Math.max(...p.hand.map(costOf)) : 0;
    yield* E.damage(G, { source: null, isAttack: false }, p, n);
  },
  k27: function* (G, _c, p) {
    const e = yield* E.pickEnemy(G, p, 'Кто получит случайную карту из твоего сброса?');
    if (e && p.discard.length) {
      const x = p.discard.splice(randInt(rngOf(G), p.discard.length), 1)[0];
      if (x) giveCard(G, p, e, x);
    }
  },
  k28: function* (G, _c, p) { for (const e of enemies(G, p)) gainChips(G, e, 1, 'жетон дохлого колдуна'); },
  k29: function* (G, _c, p) {
    const n = (yield* legendsByChoice(G, p, p.discard, 'Жетон: 4 урона за легенду в сбросе. Каких фамильяров считать легендами?', 'стопка сброса')).length;
    yield* E.damage(G, { source: null, isAttack: false }, p, 4 * n);
  },
};

/** Проверка полноты на загрузке базы: у каждой карты должен быть скрипт (или явный noop), у каждого беспредела — эффект */
export function assertScriptsComplete(cards: readonly CardId[]): void {
  const missing = cards.filter((id) => {
    const d = card(id);
    if (d.type === 'вялая палочка') return false;
    if (isBespredel(id)) return !BESPREDEL[id];
    return !CARD_EFFECTS[id];
  });
  if (missing.length) throw new Error(`Карты без скрипта: ${missing.join(', ')}`);
}
